import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

// CSS
import "../0PageStyles.css";
import "./clientCreationAndEditPage.css";

// Components
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Button from "../../Components/Other Components/button";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

// Functions and hooks
import { CreateClient, GetNextClientID } from "../../BackendAccess/ClientHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { useTranslation } from "react-i18next";

export default function ClientCreationPage() {
  const [clientData, setClientData] = useState({
    ClientID: "",
    Name: "",
    Type: "",
    Bank: "",
    BankAccount: "",
    Address: "",
    Phone: "",
    Website: "",
    SSN: "",
    TIN: "",
    PostalCode: "",
    Comment: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(false);

  const [createBtnDisabled, setCreateBtnDisabled] = useState(false);

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);
  const isNarrow = useNarrowMode();

  const navigate = useNavigate();

  const { t } = useTranslation();

  useEffect(() => {
    const fetchInitialValues = async () => {
      setLoading(true);
      try {
        const [data, error] = await fetchWithTimeout(GetNextClientID);
        if (error) {
          error.isPageError = true;
          setError(error);
        } else setClientData((prevData) => ({ ...prevData, ClientID: data }));
      } catch {
        setError({
          err: t("server.Server error.", { ns: "error" }),
          errCode: 500,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchInitialValues();
  }, []);

  const handleChange = (field) => (e) => {
    const value = e;

    setClientData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleCreate = async () => {
    setCreateBtnDisabled(true);
    showNotification(t("Creating client...", { ns: "clients" }), "info");
    try {
      const params = {
        ClientID: clientData["ClientID"],
        Name: clientData["Name"],
        Type: clientData["Type"],
        Bank: clientData["Bank"],
        BankAccount: clientData["BankAccount"],
        Address: clientData["Address"],
        Phone: clientData["Phone"],
        Website: clientData["Website"],
        SSN: clientData["SSN"],
        TIN: clientData["TIN"],
        PostalCode: clientData["PostalCode"],
        Comment: clientData["Comment"],
      };
      const [data, error] = await CreateClient(params);
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Client created successfully!", { ns: "clients" }),
          "success"
        );

        navigate(-1);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setCreateBtnDisabled(false);
    }
  };

  const handleCancel = () => {
    showNotification(t("Client creation canceled.", { ns: "clients" }), "info");
    navigate(-1);
  };

  const fields = [
    { label: "Client ID", example: "1234", mandatory: true, readOnly: true },
    { label: "Name", example: "Marc Marcus", mandatory: true },
    { label: "Type", example: "Supplier", mandatory: true },
    { label: "Bank", example: "BankName" },
    { label: "Bank Account", example: "6430000000432" },
    { label: "Address", example: "City,Street 1" },
    { label: "Phone", example: "02948291" },
    { label: "Website", example: "www.website.com" },
    { label: "SSN", example: "4392408293402" },
    { label: "TIN", example: "321-543-542-321" },
    { label: "Postal Code", example: "33222" },
  ];

  const tutorialSteps = [
    {
      target: null,
      title: t("Creating a new client", { ns: "tutorial" }),
      message: t(
        "This page adds a new client - a supplier, manufacturer, or buyer you do business with. You'll be able to link them to items and traffic notes.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-creation-essential",
      title: t("ID, Name, and Type", { ns: "tutorial" }),
      message: t(
        "The ID is auto-filled and unique for every client. Name is how the client shows up everywhere in the app. Type determines where this client can be picked - as a supplier, manufacturer, or buyer.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-creation-business",
      title: t("Business details", { ns: "tutorial" }),
      message: t(
        "All optional. Fill in whichever of these you need for your records - bank details, address, contact info, or tax numbers.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-creation-comment",
      title: t("Comment", { ns: "tutorial" }),
      message: t(
        "Optional. Add a free-text note about this client for your own reference.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-creation-create-btn",
      title: t("Save the client", { ns: "tutorial" }),
      message: t(
        "Once everything looks right, click here to create the client. It'll then show up wherever clients can be picked.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="clientCreation" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <div className="container-bordered client-creation w-90 mt-3">
            <div className="empty-container client-creation input">
              <InputGroup className="empty-container client-creation regular-fields">
                <div data-tutorial-id="client-creation-essential">
                  {fields.slice(0, 3).map((field, index) => (
                    <InputField
                      key={index}
                      placeholder={
                        t("Example:") +
                        " " +
                        t(`${field.example}`, { ns: "clients" })
                      }
                      label={t(field.label) + ":"}
                      value={clientData[field.label.replace(" ", "")]}
                      onChange={handleChange(field.label.replace(" ", ""))}
                      type={field.label === "Type" ? "dropdown" : ""}
                      options={"Clients"}
                      mandatory={field.mandatory ? field.mandatory : false}
                      readOnly={field.readOnly ? field.readOnly : false}
                    />
                  ))}
                </div>
                <div data-tutorial-id="client-creation-business">
                  {fields.slice(3).map((field, index) => (
                    <InputField
                      key={index}
                      placeholder={
                        t("Example:") +
                        " " +
                        t(`${field.example}`, { ns: "clients" })
                      }
                      label={t(field.label) + ":"}
                      value={clientData[field.label.replace(" ", "")]}
                      onChange={handleChange(field.label.replace(" ", ""))}
                      type={field.label === "Type" ? "dropdown" : ""}
                      options={"Clients"}
                      mandatory={field.mandatory ? field.mandatory : false}
                      readOnly={field.readOnly ? field.readOnly : false}
                    />
                  ))}
                </div>
                {!isNarrow ? <MandatoryExplanation /> : null}
              </InputGroup>
              <div className="empty-container client-creation comment-field" data-tutorial-id="client-creation-comment">
                <InputField
                  placeholder={t("Example: This is a very good client.", {
                    ns: "clients",
                  })}
                  label={t("Comment") + ":"}
                  value={clientData.Comment}
                  onChange={handleChange("Comment")}
                  largeField={true}
                  className="mb-1"
                />
                {isNarrow ? <MandatoryExplanation /> : null}
              </div>
            </div>
          </div>

          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-positive"
              onClick={handleCreate}
              acceptEnter={true}
              icon={"create"}
              disabled={createBtnDisabled}
              tutorialId="client-creation-create-btn"
            >
              {t("Create")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={handleCancel}
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
